import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';

import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { SortField } from '@totem/components/ui_components/reportSettings/types';
import MyWorkContext from '@totem/components/users/myWork/MyWorkContext';
import { MyWorkItem } from '@totem/components/users/types';

const TYPE_COLORS: Record<string, string> = {
  Ticket: 'blue',
  Task: 'green',
  Approval: 'orange',
  Survey: 'purple',
  'Policy Audit': 'purple',
  Building: 'cyan',
  'Sales Order Dates': 'gold',
};

const getDetailLink = (item: MyWorkItem): string => {
  switch (item.objectType) {
    case 'Ticket':
      return `/dashboard/ticket/${item.objectId}`;
    case 'Task':
    case 'Approval':
      return `/dashboard/ticket/${item.parentObjectId}`;
    case 'Policy Audit':
    case 'Survey':
      return `/dashboard/surveyinstances/${item.objectId}`;
    case 'Building':
      return `/dashboard/buildings/${item.objectId}`;
    case 'Sales Order Dates':
      return `/dashboard/salesOrder/${item.objectId}`;
    default:
      return '#';
  }
};

const TYPE_FILTER_VALUES = [
  'Ticket',
  'Task',
  'Approval',
  'Survey',
  'Policy Audit',
  'Building',
  'Sales Order Dates',
];

// Name and Description split the width left after the fixed columns ~25/75.
const TYPE_WIDTH = 110;
const PARENT_WIDTH = 140;
const ASSIGNED_WIDTH = 120;
const NAME_WIDTH = `calc((100% - ${TYPE_WIDTH + PARENT_WIDTH + ASSIGNED_WIDTH}px) * 0.25)`;

const buildColumns = (
  typeFilter: string[],
  sort: SortField,
): ColumnProps<MyWorkItem>[] => [
  {
    title: 'Type',
    dataIndex: 'objectType',
    key: 'objectType',
    width: TYPE_WIDTH,
    render: (type: string) => (
      <Tag color={TYPE_COLORS[type] ?? 'default'}>{type}</Tag>
    ),
    filters: TYPE_FILTER_VALUES.map((val) => ({
      text: val,
      value: val,
    })),
    filteredValue: typeFilter,
    onFilter: (value, record) => record.objectType === value,
  },
  {
    title: 'Name',
    dataIndex: 'objectName',
    key: 'objectName',
    width: NAME_WIDTH,
    render: (_, record) => {
      // "<organization> / <order number>": only the order number is a link
      if (
        record.objectType === 'Sales Order Dates' &&
        record.objectName.endsWith(record.objectId)
      ) {
        return (
          <>
            {record.objectName.slice(0, -record.objectId.length)}
            <Link to={getDetailLink(record)}>{record.objectId}</Link>
          </>
        );
      }
      return <Link to={getDetailLink(record)}>{record.objectName}</Link>;
    },
  },
  {
    title: 'Description',
    dataIndex: 'objectDescription',
    key: 'objectDescription',
  },
  {
    title: 'Parent',
    key: 'parent',
    width: PARENT_WIDTH,
    render: (_, record) => {
      if (!record.parentObjectId || !record.parentObjectName) {
        return null;
      }
      return (
        <Link to={`/dashboard/ticket/${record.parentObjectId}`}>
          {record.parentObjectName}
        </Link>
      );
    },
  },
  {
    title: 'Assigned',
    dataIndex: 'assigned',
    key: 'assigned',
    width: ASSIGNED_WIDTH,
    render: (assigned: string) => {
      if (!assigned) {
        return null;
      }
      return new Date(assigned).toLocaleDateString();
    },
    sorter: (compA, compB) => {
      const dateA = compA.assigned ? new Date(compA.assigned).getTime() : 0;
      const dateB = compB.assigned ? new Date(compB.assigned).getTime() : 0;
      return dateA - dateB;
    },
    sortDirections: ['ascend', 'descend'],
    sortOrder: sort.field === 'assigned' ? (sort.direction as SortOrder) : null,
  },
];

const MyWorkTable = () => {
  const { loading, data } = useContext(MyWorkContext);
  const { input, handleTableChange } = useContext(InputContext);

  // filteredValue makes the Type filter controlled. Key the memo on the
  // values' content, not the filters array: a new array reference on every
  // render makes antd fire onChange again and loops (see InputContainer).
  const typeFilterKey = (
    input.filters.find((flt) => flt.key === 'objectType')?.values ?? []
  )
    .map((item) => item.value)
    .join('|');
  const typeFilter = useMemo(
    () => (typeFilterKey === '' ? [] : typeFilterKey.split('|')),
    [typeFilterKey],
  );

  const columns = useMemo(
    () => buildColumns(typeFilter, input.sort),
    [typeFilter, input.sort],
  );

  return (
    <IBTable
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey={(record) => `${record.objectType}-${record.objectId}`}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        showSizeChanger: true,
      }}
    />
  );
};

export default MyWorkTable;
